import { db, auth } from '../lib/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getBuyerOrders, Order } from './orders';
import { getListingById } from './listings';

export interface Review {
  id?: string;
  listingId: string;
  sellerId?: string;
  reviewerId: string;
  reviewerName: string;
  rating: number; // 1-5
  comment: string;
  createdAt?: any; // Firestore Timestamp
}

export async function getListingReviews(listingId: string): Promise<Review[]> {
  const q = query(
    collection(db, 'reviews'),
    where('listingId', '==', listingId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Review) }));
}

export async function getAverageRatingForListing(listingId: string): Promise<{ average: number; count: number; }>{
  const reviews = await getListingReviews(listingId);
  const count = reviews.length;
  const average = count > 0 ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / count) : 0;
  return { average, count };
}

export async function getAverageRatingForSeller(sellerId: string): Promise<{ average: number; count: number }>{
  const q = query(
    collection(db, 'reviews'),
    where('sellerId', '==', sellerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  const reviews = snap.docs.map(d => d.data() as Review);
  const count = reviews.length;
  const average = count > 0 ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / count) : 0;
  return { average, count };
}

export async function getSellerReviews(sellerId: string): Promise<Review[]>{
  const q = query(
    collection(db, 'reviews'),
    where('sellerId', '==', sellerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Review) }));
}

/**
 * Check if a user has purchased a specific product
 */
export async function hasUserPurchasedProduct(userId: string, listingId: string): Promise<Order | null> {
  try {
    const orders = await getBuyerOrders(userId);
    
    // Find a completed order that contains this listing
    const completedOrder = orders.find(order => 
      order.status === 'completed' && 
      order.completedAt && 
      order.orderItems?.some(item => item.listingId === listingId)
    );
    return completedOrder || null;
  } catch (error) {
    console.error('Error checking purchase status:', error);
    return null;
  }
}

export async function addReview(listingId: string, rating: number, comment: string): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  // Check if user has purchased this product and if the order is completed
  const completedOrder = await hasUserPurchasedProduct(user.uid, listingId);
  if (!completedOrder) {
    throw new Error('You can only review products you have purchased after the order is completed.');
  }

  // Calculate the reviewable date (3 days after order completion)
  const completedAtMillis = completedOrder.completedAt.toMillis();
  const threeDaysInMillis = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds
  const reviewableDate = new Date(completedAtMillis + threeDaysInMillis);

  // Check if the current date is past the reviewable date
  if (new Date() < reviewableDate) {
    const remainingDays = Math.ceil((reviewableDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    throw new Error(`You can only review this product ${remainingDays} day(s) after the order is completed.`);
  }

  // Get listing to attach sellerId to the review for seller-level ratings
  const listing = await getListingById(listingId);
  if (!listing || !listing.sellerId) {
    throw new Error('Listing not found or missing seller information');
  }
  
  const payload: Omit<Review, 'id'> = {
    listingId,
    sellerId: listing.sellerId,
    reviewerId: user.uid,
    reviewerName: user.displayName || 'Unknown User',
    rating: Math.max(1, Math.min(5, Math.round(rating))),
    comment: comment.trim(),
    createdAt: serverTimestamp()
  };
  const ref = await addDoc(collection(db, 'reviews'), payload);
  return ref.id;
}